import { Box, Divider, Paper, Typography, useTheme, FormControl, MenuItem, Select } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../../theme/style/graphChartPalette";
import DonutChart from "../../../components/chart/DonutChart";
import HorizontalStackedBarChart from "../../../components/chart/HorizontalStackedBarChart";

const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

const filterSelectSx = (isDark, accentColor) => ({
  ...cellTextSx(isDark),
  minWidth: 160,
  "& .MuiSelect-select": { py: "4px", px: 0, pb: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  borderBottom: `2px solid ${accentColor}`,
  borderRadius: 0,
  bgcolor: "transparent",
});

const COLORS = ["#10b981", "#ef4444", "#3b82f6", "#f97316", "#a855f7", "#ec4899", "#14b8a6", "#eab308"];

const LoginGlobalView = ({ 
    loginHistory = [], 
    analytics = null,
    accentColor = "#16a34a" 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [filterOrg, setFilterOrg] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  const orgOptions = useMemo(() => {
    return [...new Set(loginHistory.map(u => u.company_name).filter(Boolean))].sort();
  }, [loginHistory]);

  const filteredData = useMemo(() => {
    return loginHistory.filter(u => {
      if (filterOrg !== "All" && u.company_name !== filterOrg) return false;
      if (filterStatus !== "All") {
        const success = u.is_attempt_successful === 1 || u.is_attempt_successful === true;
        if (filterStatus === "Success" && !success) return false;
        if (filterStatus === "Failed" && success) return false;
      }
      return true;
    });
  }, [loginHistory, filterOrg, filterStatus]);

  const statusDonutData = useMemo(() => {
    const successCount = filteredData.filter(u => u.is_attempt_successful === 1 || u.is_attempt_successful === true).length;
    const failedCount = filteredData.length - successCount;

    return [
      { label: t("Success"), value: successCount, color: COLORS[0], percent: filteredData.length > 0 ? (successCount / filteredData.length) * 100 : 0 },
      { label: t("Failed"), value: failedCount, color: COLORS[1], percent: filteredData.length > 0 ? (failedCount / filteredData.length) * 100 : 0 }
    ].filter(d => d.value > 0);
  }, [filteredData, t]);

  const barChartData = useMemo(() => {
    const orgTotals = {};
    filteredData.forEach((u) => {
      const key = u.company_name || t("Unknown");
      orgTotals[key] = (orgTotals[key] || 0) + 1;
    });
    return Object.entries(orgTotals)
      .map(([label, count]) => ({ label, Attempts: count }))
      .sort((a, b) => b.Attempts - a.Attempts)
      .slice(0, 10);
  }, [filteredData, t]);

  return (
    <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
      <Box sx={{ mb: tok.sectionHeading.containerMb, pb: tok.sectionHeading.titlePb, borderBottom: tok.sectionHeading.borderBottom, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{
            fontWeight: tok.sectionHeading.fontWeight,
            color: tok.sectionHeading.color,
            fontFamily: tok.sectionHeading.fontFamily,
            fontSize: tok.sectionHeading.fontSize,
            lineHeight: 1.2,
            borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`,
            pl: 1,
          }}>
            {t("Authentication Insights")}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <FormControl variant="outlined" size="small">
            <Select value={filterOrg} onChange={(e) => setFilterOrg(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
              <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Organizations")}</MenuItem>
              {orgOptions.map((name) => (
                <MenuItem key={name} value={name} sx={cellTextSx(isDark)}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small">
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
              <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Statuses")}</MenuItem>
              <MenuItem value="Success" sx={cellTextSx(isDark)}>{t("Success")}</MenuItem>
              <MenuItem value="Failed" sx={cellTextSx(isDark)}>{t("Failed")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: tok.spacing.pageGap }}>
        <Box sx={{ flex: "1 1 60%", minWidth: 450 }}>
          <Paper sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Top Organizations by Login Activity")}
              </Typography>
            </Box>
            {barChartData.length > 0 ? (
              <HorizontalStackedBarChart
                data={barChartData}
                dataKeys={["Attempts"]}
                getColor={() => accentColor}
                showLegend={false}
                height={Math.max(barChartData.length * 45 + 100, 350)}
                barSize={20}
                xAxisLabel={t("Number of Attempts")}
                yAxisLabel={t("Organizations")}
              />
            ) : (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center", opacity: 0.5 }}>
                <Typography sx={cellTextSx(isDark)}>{t("No activity data available.")}</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ flex: "1 1 30%", minWidth: 350 }}>
          <Paper sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
            <Box sx={{ mb: 3, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Login Status Distribution")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <DonutChart
                data={statusDonutData}
                size={200}
                strokeWidth={22}
                centerContent={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b" }}>{t("Total")}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: GRAPH_PALETTE.font.weights.heavy, color: tok.text.primary }}>
                      {(analytics?.total_logins ?? filteredData.length).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
            </Box>
            {statusDonutData.map((d, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color }} />
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, color: tok.text.primary }}>
                      {d.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, color: tok.text.primary }}>
                    {d.value.toLocaleString()} ({Math.round(d.percent)}%)
                  </Typography>
                </Box>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color }} />
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginGlobalView;
